/**
 * Device Pending Dialog
 *
 * Displayed when a device is pending manager approval.
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { HourglassEmpty as PendingIcon } from '@mui/icons-material';

interface DevicePendingDialogProps {
  open: boolean;
  status: 'pending' | 'inactive' | 'revoked';
  message?: string;
  onClose: () => void;
}

const statusConfig = {
  pending: {
    icon: <PendingIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
    title: 'Device Awaiting Approval',
    severity: 'warning' as const,
    defaultMessage:
      'This device is awaiting manager approval. You will be able to log in once your manager approves this device.',
  },
  inactive: {
    icon: <PendingIcon sx={{ fontSize: 48, color: 'error.main' }} />,
    title: 'Device Deactivated',
    severity: 'error' as const,
    defaultMessage:
      'This device has been deactivated by your manager. Please contact your manager if you need to use this device.',
  },
  revoked: {
    icon: <PendingIcon sx={{ fontSize: 48, color: 'error.main' }} />,
    title: 'Device Revoked',
    severity: 'error' as const,
    defaultMessage:
      'This device has been permanently revoked for security reasons. Please contact your administrator.',
  },
};

export function DevicePendingDialog({
  open,
  status,
  message,
  onClose,
}: DevicePendingDialogProps) {
  const config = statusConfig[status];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {config.icon}
          {config.title}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity={config.severity} sx={{ mt: 2 }}>
          <Typography variant="body1">{message || config.defaultMessage}</Typography>
        </Alert>

        {status === 'pending' && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>What happens next?</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1 }}>
              <li>Your manager will review your device registration request</li>
              <li>Once approved, you&apos;ll be able to log in from this device</li>
              <li>You may contact your manager to expedite the approval</li>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
